<script setup lang="ts">
import {
  BlobModel,
  CaseModel,
  FormFieldChoice,
  type FormField
} from '@/models';
import { useSurgeonDashboardStore } from '@/stores/surgeonDashboard';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import CaseSelector from '../CaseSelector.vue';
import SurgeonDashboardInfoCell from '../SurgeonDashboardInfoCell.vue';
import InteractiveGraph from './InteractiveGraph.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import type {
  GraphFilter,
  GraphFilterGroup,
  GraphGroup
} from '@/models/surgeonDashboard';
import { useRoute, useRouter } from 'vue-router';
import ProgressSpinner from 'primevue/progressspinner';
import {
  DashboardGraphFilters,
  elapsedTimeText,
  routeConstants
} from '@/utils';
import { ButtonType, CustomButton } from '@/components/common';
import { DateTime } from 'luxon';
import { useI18n } from 'vue-i18n';
import Galleria from 'primevue/galleria';
import { useStudyStore } from '@/stores/study';
import CaseNotesPanel from '@/components/data/pipeline/CaseNotesPanel.vue';
import CaseNoteDialog from '@/components/data/pipeline/CaseNoteDialog.vue';
import { useCaseNoteStore } from '@/stores/caseNote';
import type { TimepointOption } from '@/models/caseNote';
import {
  useConnnectionStore,
  type CaseNoteSocketEvent
} from '@/stores/connection';

const { t: $t } = useI18n();
const surgeonStore = useSurgeonDashboardStore();
const studyStore = useStudyStore();
const router = useRouter();
const isMobile = ref(window.matchMedia('(max-width: 768px)').matches);

const perfusionSummary = ref<FormField[]>([]);
const route = useRoute();
const initialCaseId = ref();
const currentCaseId = ref<string>();
const selectedCaseModel = ref<CaseModel>();
const isPanelPinned = ref(false);
const showNotesPanel = ref(false);
const showAddNoteDialog = ref(false);
const caseNoteStore = useCaseNoteStore();
const connectionStore = useConnnectionStore();
const hasUnreadCaseNoteActivity = ref(false);
let unsubscribeCaseNoteEvents: (() => void) | undefined;

const notesColumnVisible = computed(() =>
  showNotesPanel.value && isPanelPinned.value && canShowCaseNotes.value && !!selectedCaseModel.value
);

const noteTimepointOptions = computed<TimepointOption[]>(() => {
  return (surgeonStore.interactiveDashboardData?.timeSeriesTimepoints ?? []).map(tp => ({
    label: tp.label,
    value: `${tp.value}`
  }));
});

const handleAddNoteSaved = async () => {
  showAddNoteDialog.value = false;
  if (selectedCaseModel.value) {
    await caseNoteStore.getNotes(selectedCaseModel.value.identifier);
    selectedCaseModel.value.caseNoteCount = caseNoteStore.notes.length;
  }
};

const syncCaseNotesForCurrentCase = async () => {
  if (!selectedCaseModel.value) return;
  await caseNoteStore.getNotes(selectedCaseModel.value.identifier);
  selectedCaseModel.value.caseNoteCount = caseNoteStore.notes.length;
};

const handleCaseNoteSocketEvent = async (event: CaseNoteSocketEvent) => {
  if (!selectedCaseModel.value) return;
  if (event.caseIdentifier !== selectedCaseModel.value.identifier) return;
  await syncCaseNotesForCurrentCase();
  if (!showNotesPanel.value) {
    hasUnreadCaseNoteActivity.value = true;
  }
};

const canShowCaseNotes = computed(() => {
  const flags = studyStore.selectedStudy?.flags as
    | Record<string, unknown>
    | undefined;
  return flags?.['enableCaseNotes'] === true;
});

const interactiveModeEnabled = ref(true);
const graphGroups = ref<GraphGroup[]>([]);
const lastRefreshed = ref(DateTime.now());
const now = ref(DateTime.now());
const showGalary = ref<boolean>(false);
const galaryImageIndex = ref<number>(0);
const galaryImages = ref<BlobModel[]>([]);

const filterGroups = ref<GraphFilterGroup[]>(DashboardGraphFilters);

const handleLiverPerKgChange = async (showLiverPerKg: boolean, field: FormField) => {
  const graphGroupIndex = graphGroups.value.findIndex(group =>
    group.data?.some(f => f.identifier === field.identifier)
  );

  if (graphGroupIndex === -1) return;

  const groupData = graphGroups.value[graphGroupIndex].data!;
  const fieldIndex = groupData.findIndex(f => f.identifier === field.identifier);

  if (fieldIndex === -1) return;

  // Create new arrays to ensure reactivity for the loading state
  const newGroupDataLoading = [...groupData];
  newGroupDataLoading[fieldIndex] = {
    ...newGroupDataLoading[fieldIndex],
    loading: true
  };
  const newGraphGroupsLoading = [...graphGroups.value];
  newGraphGroupsLoading[graphGroupIndex] = {
    ...newGraphGroupsLoading[graphGroupIndex],
    data: newGroupDataLoading
  };
  graphGroups.value = newGraphGroupsLoading;

  const selectedFilters = filterGroups.value
    .map(group => group.filters.filter(fil => fil.selected))
    .flat();
  const postFilters: Record<string, string> = {};
  selectedFilters?.forEach(filter => {
    postFilters[filter.key] = filter.value;
  });
  try {
    const updatedField = await surgeonStore.getInteractiveVariableData(
      currentCaseId.value!,
      field,
      {
        ...postFilters,
        Adjustment: showLiverPerKg ? `Per liver kg` : 'Unadjusted'
      }
    );

    if (updatedField) {
      const liverWeightBeforePerfusion = surgeonStore.interactiveDashboardData?.perfusionSummaryData?.find(
        data => data.variableName === 'LIVER_WEIGHT_BEFORE_PERFUSION'
      );
      const liverWeight = parseFloat(liverWeightBeforePerfusion?.value || '0');

      const newTimeSeriesData = field.timeSeriesData?.map(ts => {
        const originalValue = ts.value;
        let newValue = originalValue;

        if (liverWeight > 0) {
          if (!field.perLiverKgOptionChecked) {
            // Apply the 'per kg' adjustment
            newValue = (originalValue / liverWeight) * 1000;
          } else {
            // Reverse the 'per kg' adjustment
            newValue = (originalValue * liverWeight) / 1000;
          }
        }
        
        return {
          ...ts,
          value: newValue
        };
      }) || [];
      // Create a new array for the graph data with the updated field
      const newGroupData = [...graphGroups.value[graphGroupIndex].data!];
      newGroupData[fieldIndex] = {
        ...newGroupData[fieldIndex],
        historicalData: updatedField.historicalData,
        centerHistoricData: updatedField.centerHistoricData,
        timeSeriesData: newTimeSeriesData,
        perLiverKgOptionChecked: !field.perLiverKgOptionChecked,
        loading: false,
        dataVersion: (newGroupData[fieldIndex].dataVersion || 1) + 1
      };

      // Create a new graphGroups array to ensure reactivity
      const newGraphGroups = [...graphGroups.value];
      newGraphGroups[graphGroupIndex] = {
        ...newGraphGroups[graphGroupIndex],
        data: newGroupData
      };

      graphGroups.value = newGraphGroups;
    }
  } catch (error) {
    // console.error('Error updating liver per kg data:', error);
  } finally {
    // Ensure loading state is turned off in case of error
    const newGroupDataFinal = [...graphGroups.value[graphGroupIndex].data!];
    newGroupDataFinal[fieldIndex] = {
      ...newGroupDataFinal[fieldIndex],
      loading: false
    };
    const newGraphGroupsFinal = [...graphGroups.value];
    newGraphGroupsFinal[graphGroupIndex] = {
      ...newGraphGroupsFinal[graphGroupIndex],
      data: newGroupDataFinal
    };
    graphGroups.value = newGraphGroupsFinal;
  }
};

const handleFilterClick = async (filter: GraphFilter) => {
  surgeonStore.isDashboardLoading.startLoading();
  if (filter.value === 'Unadjusted' || filter.value === 'Per liver kg') {
    //special handling for adjustment filters
    filterGroups.value
      .find(group => group.id == 6)!
      .filters.forEach(fil => {
        if (fil.value == filter.value) {
          fil.selected = true;
        } else {
          fil.selected = false;
        }
      });
  } else if (filter.value === 'all') {
    for (const group of filterGroups.value) {
      group.filters.forEach(fil => {
        if (group.id !== 6) {
          fil.selected = group.id === 0;
        }
      });
    }
  } else {
    //unselect `all` filer first
    filterGroups.value
      .find(group => group.id === 0)!
      .filters.forEach(fil => (fil.selected = false));
    for (const group of filterGroups.value) {
      for (const fil of group.filters) {
        if (fil.value === filter.value) {
          fil.selected = !filter.selected;
          group.filters.forEach(groupFil => {
            if (groupFil.label != fil.label) groupFil.selected = false;
          });
          break;
        }
      }
    }
  }
  const selectedFilters = filterGroups.value
    .map(group => group.filters.filter(fil => fil.selected))
    .flat();

  await surgeonStore.getInteractiveSurgeonDashboardData(
    currentCaseId.value!,
    selectedFilters
  );

  graphGroups.value = [];
  setTimeout(() => {
    graphGroups.value = [
      {
        label: 'Perfusion summary',
        expanded: true,
        data: mapDataWithVersion(surgeonStore.interactiveDashboardData!.gases)
      },
      {
        label: 'NMP Labs',
        expanded: false,
        data: mapDataWithVersion(surgeonStore.interactiveDashboardData!.labs)
      },
      {
        label: 'NMP Bile',
        expanded: false,
        data: mapDataWithVersion(surgeonStore.interactiveDashboardData!.bile)
      },
      {
        label: 'Machine parameters',
        expanded: false,
        data: mapDataWithVersion(surgeonStore.interactiveDashboardData!.flows)
      }
    ];
  }, 100);
  surgeonStore.isDashboardLoading.stopLoadingAndFinish();
};

const elapsedText = computed(() => {
  return elapsedTimeText(lastRefreshed.value, now.value);
});

let timer: ReturnType<typeof setInterval>;

onUnmounted(() => {
  clearInterval(timer);
  unsubscribeCaseNoteEvents?.();
});

onMounted(() => {
  initialCaseId.value =
    (route.params.caseId || route.params.id || route.params.identifier) &&
    (route.params.caseId || route.params.id || route.params.identifier) !==
      'undefined' ?
      (route.params.caseId || route.params.id || route.params.identifier) :
      undefined;
  interactiveModeEnabled.value = route.query.interactive !== `false`;
  if (surgeonStore.perfusionSummaryData) {
    perfusionSummary.value = surgeonStore.perfusionSummaryData;
  }

  setAxisData(route.query);
  timer = setInterval(() => {
    now.value = DateTime.now();
  }, 60000);

  connectionStore.connect();
  unsubscribeCaseNoteEvents = connectionStore.subscribeToCaseNoteEvents(event => {
    void handleCaseNoteSocketEvent(event);
  });
});


const mapDataWithVersion = (data: FormField[] | undefined) => 
  data?.map(field => ({ ...field, dataVersion: (field.dataVersion || 0) + 1 })) || [];

const onPerfusionSelection = async (caseModel: CaseModel) => {
  hasUnreadCaseNoteActivity.value = false;
  selectedCaseModel.value = caseModel;
  currentCaseId.value = caseModel.identifier;
  caseNoteStore.requestPinnedPanelCase(caseModel.identifier, caseModel.label);
  router.replace({
    name: routeConstants.CUSTOM_INTERACTIVE_SURGEON_DASHBOARD.name,
    params: { caseId: caseModel.identifier },
    query: {
      ...route.query,
      interactive:
        route.query.interactive === `false` ||
        route.query.interactive === `true` ?
          `${route.query.interactive}` :
          undefined
    }
  });
  perfusionSummary.value = [];
  await surgeonStore.getInteractiveSurgeonDashboardData(caseModel.identifier);
  if (!surgeonStore.interactiveDashboardData) {
    return;
  }

  if (surgeonStore.interactiveDashboardData?.perfusionSummaryData) {
    perfusionSummary.value =
      surgeonStore.interactiveDashboardData!.perfusionSummaryData;
  }

  graphGroups.value = [
    {
      label: 'Perfusion summary',
      expanded: true,
      data: mapDataWithVersion(surgeonStore.interactiveDashboardData!.gases)
    },
    {
      label: 'NMP Labs',
      expanded: false,
      data: mapDataWithVersion(surgeonStore.interactiveDashboardData!.labs)
    },
    {
      label: 'NMP Bile',
      expanded: false,
      data: mapDataWithVersion(surgeonStore.interactiveDashboardData!.bile)
    },
    {
      label: 'Machine parameters',
      expanded: false,
      data: surgeonStore.interactiveDashboardData!.flows
    }
  ];
};

const expandGraphGroup = (graphGroup: GraphGroup) => {
  const group = graphGroups.value.find(
    graph => graph.label == graphGroup.label
  );
  if (group) {
    group.expanded = !group.expanded;
  }
};

const switchToVitalSigns = () => {
  router.push({
    name: routeConstants.CASE_SURGEON_DASHBOARD.name,
    params: { caseId: currentCaseId.value }
  });
};

const onRefresh = () => {
  const caseItem =
    selectedCaseModel.value || CaseModel.createEmpty(currentCaseId.value!);
  onPerfusionSelection(caseItem);
};

const onLiverImageClick = (fileIndex: number, variableName: string) => {
  surgeonStore.loadFullResolutionImages(variableName);
  showGalary.value = true;
  galaryImageIndex.value = fileIndex;
  const imageField = surgeonStore.summaryLiverImages.find(
    field => field.variableName === variableName
  );
  if (imageField && imageField.files) {
    galaryImages.value = imageField.files;
  }
};

const hasNmPStartDate = computed(() => {
  const date = surgeonStore.interactiveDashboardData?.perfusionSummaryData?.find(
    data => data.variableName == 'NMP_START_DATE_TIME'
  )?.value;
  return  date != undefined && date != 'No data';
});

// Helper to safely parse numeric query values (handles arrays and non-numeric values)
const parseNumberQueryValue = (value: unknown): number | undefined => {
  // Vue Router query values can be string | string[] | null | undefined
  if (Array.isArray(value)) {
    value = value[0];
  }
  if (typeof value !== 'string' && typeof value !== 'number') {
    return undefined;
  }
  const num = typeof value === 'number' ? value : parseFloat(value);
  return Number.isFinite(num) ? num : undefined;
};

// @ts-expect-error - newQuery is dynamic and can have various keys based on the filters applied, so we can't strictly type it here
const setAxisData = newQuery => {
  for (const group of graphGroups.value) {
    group.data?.forEach(field => {
      const yMin = parseNumberQueryValue(newQuery[`${field.variableName}-y-min`]);
      if (yMin !== undefined) {
        field.graphData = {
          ...field.graphData,
          yAxisMin: yMin
        };
      } 
      const yMax = parseNumberQueryValue(newQuery[`${field.variableName}-y-max`]);
      if (yMax !== undefined) {
        field.graphData = {
          ...field.graphData,
          yAxisMax: yMax
        };
      }
      const yStep = parseNumberQueryValue(newQuery[`${field.variableName}-y-step`]);
      if (yStep !== undefined) {
        field.graphData = {
          ...field.graphData,
          yAxisStep: yStep
        };
      }
      const xMin = parseNumberQueryValue(newQuery[`${field.variableName}-x-min`]);
      if (xMin !== undefined) {
        field.graphData = {
          ...field.graphData,
          xAxisMin: xMin
        };
      }
      const xMax = parseNumberQueryValue(newQuery[`${field.variableName}-x-max`]);
      if (xMax !== undefined) {
        field.graphData = {
          ...field.graphData,
          xAxisMax: xMax
        };
      }
      const xStep = parseNumberQueryValue(newQuery[`${field.variableName}-x-step`]);
      if (xStep !== undefined) {
        field.graphData = {
          ...field.graphData,
          xAxisStep: xStep
        };
      }
    });
  }
  interactiveModeEnabled.value = newQuery.interactive !== `false`;
};

watch(() => graphGroups.value, groups => {
  if (groups && groups.length > 0) {
    setAxisData(route.query);
  }
});

watch(() => route.query, newQuery => {
  setAxisData(newQuery);
});

watch(
  () => showNotesPanel.value,
  isOpen => {
    if (isOpen) hasUnreadCaseNoteActivity.value = false;
  }
);
</script>

<template>
  <div class="custom-interactive-dashboard-layout" :class="{ 'notes-open': notesColumnVisible }">
    <div class="dashboard">
    <div class="side-toolbar"
      v-if="canShowCaseNotes && selectedCaseModel && !showNotesPanel">
      <div class="toolbar-note-wrapper">
        <button type="button"
          class="toolbar-btn"
          title="Case notes"
          @click="showNotesPanel = true">
          <FontAwesomeIcon :icon="['far', 'note-sticky']" />
        </button>
        <span class="note-toolbar-activity-dot"
          v-if="hasUnreadCaseNoteActivity"
          title="New note activity"></span>
        <span class="note-toolbar-badge"
          v-if="(selectedCaseModel?.caseNoteCount ?? 0) > 0">
          {{ (selectedCaseModel?.caseNoteCount ?? 0) > 99 ? '99+' : selectedCaseModel?.caseNoteCount }}
        </span>
      </div>
    </div>

    <div class="dashboard-data">
      <div class="elapsed-time">
        {{ elapsedText }}
        <div @click="onRefresh" class="perfusion-refresh-ellapsed" v-ripple>
          <FontAwesomeIcon :icon="['far', 'arrow-rotate-right']" />
        </div>
      </div>
      <div class="perfusion-info-container">
        <CustomButton text="Back"
          @onClick="switchToVitalSigns"
          :margin-left="16"
          v-if="interactiveModeEnabled"
          :type="ButtonType.OUTLINED"/>
        <div class="perfusion-selector">
          <div class="prefusion-selector-label">Current Perfusion:</div>
          <CaseSelector @onCasesFetched="onPerfusionSelection"
            @onCaseChanged="onPerfusionSelection"
            :initialCaseId="initialCaseId"
            :whatFor="'surgeon-dashboard'"/>
        </div>
        <div class="perfusion-info" v-if="perfusionSummary">
          <div class="responsive-grid">
            <div v-for="item in perfusionSummary"
              :key="item.identifier"
              class="grid-item">
              <SurgeonDashboardInfoCell :title="item.label"
                :value="item.value"/>
            </div>
          </div>
        </div>
        <div class="summary-tables">
          <div class="table"
            v-for="tableField in surgeonStore.summaryTables"
            :key="tableField.identifier">
            <h3 class="title">{{ tableField.label }}</h3>
            <div class="matrix-table-wrapper">
              <table class="matrix-table">
                <tbody>
                  <tr v-for="column in tableField.options.columns"
                    :key="column.id">
                    <th>{{ column.label }}</th>
                    <td v-for="row in tableField.matrixTableRowValues"
                      :key="row.rowId">
                      {{
                        row.columnValues?.find(
                          (col) => `${column.id}` === `${col.id}`
                        )?.textValue ||
                        row.columnValues?.find(
                          (col) => `${column.id}` === `${col.id}`
                        )?.numberValue ||
                        (
                          row.columnValues?.find(
                            (col) => `${column.id}` === `${col.id}`
                          )?.choiceValue as FormFieldChoice
                        )?.value ||
                        "-"
                      }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div class="images" v-if="surgeonStore.summaryLiverImages.length > 0">
          <div class="liver-images-container"
            v-for="imageField in surgeonStore.summaryLiverImages"
            :key="imageField.identifier">
            <h3 class="liver-image-title">{{ imageField.label }}</h3>
            <div class="images-container">
              <div class="liver-images">
                <div class="file"
                  v-for="(file, index) in imageField.files"
                  :key="file.identifier">
                  <div class="thumbnail"
                    :class="{ 'is-loading': file.isLoading }"
                    @click="
                      !file.isLoading &&
                        onLiverImageClick(index, imageField.variableName!)
                    ">
                    <div class="image-loader"
                      v-if="file.isLoading"></div>
                    <FontAwesomeIcon :icon="['far', 'file']"
                      v-if="!file.isLoading && !file.hasThumbnail"
                      class="file-icon"/>
                    <img :src="file.thumbnailKey"
                      class="thumbnail-img"
                      v-if="!file.isLoading && file.hasThumbnail"/>
                  </div>
                  <div class="thumbnail-details">
                    <div class="name">{{ file.name }}</div>
                  </div>
                  <div class="thumbnail-details" v-if="file.label">
                    <div class="name">{{ file.label }}</div>
                  </div>
                </div>
                <div class="liver-images-padding"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="warning"
      v-if="
        surgeonStore.interactiveDashboardData &&
        !surgeonStore.isDashboardLoading.isLoading
      ">
      <h3 class="warning-title">
        <FontAwesomeIcon :icon="['fas', 'triangle-exclamation']"
          class="warning-icon"/>
        Important Note:
      </h3>
      <div>{{ $t("surgeon_dashboard_warning") }}</div>
    </div>

    <div class="no-perfusion-data"
      v-if="!hasNmPStartDate && !surgeonStore.isDashboardLoading.isLoading">
      Cannot display the graphs until the NMP start date is submitted.
    </div>

    <div class="filters-container"
      v-if="!surgeonStore.isDashboardLoading.isLoading && hasNmPStartDate">
      <div class="filters"
        v-for="filterGroup in filterGroups"
        :key="filterGroup.id">
        <div class="filter-group"
          v-for="filter in filterGroup.filters"
          :key="filter.key">
          <div :class="`filter-button ${
              filter.selected ? 'selected-filter-button' : ''
            }`"
            @click="handleFilterClick(filter)"
            v-ripple>
            {{ filter.label }}
          </div>
        </div>
      </div>
    </div>
    <div class="graph-root" v-if="!surgeonStore.isDashboardLoading.isLoading && hasNmPStartDate">
      <div class="graphs"
        v-for="graphGroup in graphGroups"
        :key="graphGroup.label">
        <div class="graph-title-container"
          @click="expandGraphGroup(graphGroup)"
          v-ripple>
          <h2 class="graph-title">{{ graphGroup.label }}</h2>
          <div class="down-arrow">
            <FontAwesomeIcon :icon="['fas', 'chevron-down']" v-ripple />
          </div>
        </div>

        <div class="chart"
          v-if="
            graphGroup.expanded &&
            surgeonStore.interactiveDashboardData?.timeSeriesTimepoints
          ">
          <div v-for="field in graphGroup.data"
            :key="`${field.identifier}-${field.dataVersion || 1}`">
            <InteractiveGraph :field="field"
              :case-id="currentCaseId"
              @liverKgDataChange="handleLiverPerKgChange"
              :time-series-timepoints="
                surgeonStore.interactiveDashboardData!.timeSeriesTimepoints
              "/>
          </div>
        </div>
      </div>
    </div>

    <div class="loader" v-if="surgeonStore.isDashboardLoading.isLoading">
      <ProgressSpinner strokeWidth="4" />
    </div>

    <div class="gallery" v-if="showGalary">
      <Galleria v-model:visible="showGalary"
        :value="galaryImages"
        :activeIndex="galaryImageIndex"
        :responsiveOptions="surgeonStore.responsiveOptions"
        :numVisible="2"
        :containerStyle="
          isMobile
            ? { maxWidth: '100%' }
            : { maxWidth: '50%', backgroundColor: '#e5e7eb' }
        "
        :circular="true"
        :fullScreen="true"
        :showItemNavigators="true">
        <template #item="slotProps">
          <img :style="
              isMobile || !slotProps.item.downloadedBlob
                ? { width: '100%', display: 'block' }
                : { width: '50%', display: 'block' }
            "
            :src="slotProps.item.downloadedBlob || slotProps.item.thumbnailKey"/>
        </template>
        <template #thumbnail="slotProps">
          <img :src="slotProps.item.thumbnailKey"
            :alt="slotProps.item.alt"
            style="display: block; width: 83px"/>
        </template>
        <template #closeicon>
          <FontAwesomeIcon :icon="['fas', 'close']" class="gallery-close" />
        </template>
      </Galleria>
    </div>
    </div>

    <div class="notes-column" v-show="notesColumnVisible">
      <CaseNotesPanel :visible="showNotesPanel"
        :inline="isPanelPinned"
        :show-pin-button="true"
        :case-identifier="selectedCaseModel?.identifier ?? ''"
        :case-label="selectedCaseModel?.label ?? ''"
        :timepoint-options="noteTimepointOptions"
        @onClose="showNotesPanel = false; isPanelPinned = false"
        @onAddNote="showAddNoteDialog = true"
        @onPinChange="isPanelPinned = $event"/>
    </div>
  </div>

  <CaseNoteDialog :visible="showAddNoteDialog"
    :case-identifier="selectedCaseModel?.identifier ?? ''"
    :timepoint-options="noteTimepointOptions"
    @onClose="showAddNoteDialog = false"
    @onSaved="handleAddNoteSaved"/>
</template>

<style lang="scss" scoped>
@use "../../../assets/variables.module" as *;
.custom-interactive-dashboard-layout {
  display: flex;
  height: 100%;
  overflow: hidden;

  .dashboard {
    flex: 1;
    min-width: 0;
  }

  .notes-column {
    width: 420px;
    flex-shrink: 0;
    height: 100%;
    border-left: 1px solid var(--color-border);
    background: #fff;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    padding: 16px;
    box-sizing: border-box;
  }
}

.dashboard {
  height: 100%;
  overflow-y: scroll;
  padding: 32px;
  padding-top: 12px;
}
.perfusion-info-container {
  border-radius: 8px;
  background-color: white;
  padding: 16px;

  .perfusion-selector {
    display: flex;
    align-items: center;
    padding: 16px;

    .prefusion-selector-label {
      min-width: 150px;
    }
  }

  .responsive-grid {
    display: grid;
    gap: 16px;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    padding: 16px;
  }
}
.perfusion-refresh-ellapsed {
  border: solid 1px hsla(0, 0%, 0%, 0.25);
  border-radius: 4px;
  margin-left: 8px;
  padding: 2px 4px;
  cursor: pointer;
  &:hover {
    background-color: #f0f0f0;
  }
}
.elapsed-time {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 8px;
  margin-bottom: 8px;
}

.side-toolbar {
  position: fixed;
  right: 0;
  top: 70px;
  z-index: 100;
  display: flex;
  flex-direction: column;
  gap: 4px;
  background: var(--color-background, #fff);
  border: 1px solid var(--color-border, #e0e0e0);
  border-right: none;
  border-radius: 6px 0 0 6px;
  padding: 6px 4px;
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.06);
}

.toolbar-note-wrapper {
  position: relative;
  display: inline-flex;
}

.note-toolbar-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 12px;
  height: 12px;
  padding: 0 2px;
  border-radius: 8px;
  background: var(--p-primary-color, #{$primary-color});
  color: #fff;
  font-size: 10px;
  font-weight: 600;
  line-height: 12px;
  text-align: center;
  pointer-events: none;
  z-index: 1;
}

.note-toolbar-activity-dot {
  position: absolute;
  bottom: -2px;
  left: -2px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #ff8a00;
  border: 1px solid #fff;
  box-shadow: 0 0 0 2px rgba(255, 138, 0, 0.25);
  pointer-events: none;
  z-index: 2;
}

.toolbar-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  padding: 0;
  border: none;
  border-radius: 5px;
  background: transparent;
  color: rgba(0, 0, 0, 0.5);
  cursor: pointer;
  font-size: 34px;
  line-height: 1;
  transition: background 0.15s ease, color 0.15s ease;

  :deep(svg) {
    width: 34px;
    height: 34px;
  }

  &:hover {
    background: var(--color-background-mute, #f0f0f0);
    color: var(--color-text, #222);
  }
}

.graphs {
  margin-top: 24px;
  background-color: white;
  padding-left: 16px;
  padding-right: 16px;
  border-radius: 8px;
}
.graph-title-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 16px;
  padding-bottom: 16px;
  cursor: pointer;

  .down-arrow {
    font-size: 16px;
    padding: 8px;
  }
}
.chart {
  display: grid;
  gap: 24px;
  grid-template-columns: repeat(auto-fit, minmax(550px, 1fr));
}
.warning {
  margin-top: 16px;
  border: solid 1px hsla(0, 0%, 0%, 0.25);
  border-radius: 4px;
  padding: 8px;
}
.warning-title {
  font-weight: 700;
}
.warning-icon {
  font-size: 20px;
  color: #ffce04;
}
.gallery-close {
  font-size: 24px;
  color: black;
}
.table {
  padding-left: 16px;
  padding-right: 16px;
}
.images-container {
  max-width: 100%;
  overflow-x: auto;
}
.matrix-table-wrapper {
  max-width: 100%;
  overflow-x: auto;
}
.matrix-table {
  border-collapse: collapse;
  margin: 16px 0;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);

  th,
  td {
    border: 1px solid #e0e0e0;
    padding: 10px 14px;
    text-align: left;
    min-width: 120px;
    max-width: 220px;
    word-break: break-word;
    white-space: pre-line;
    font-size: 15px;
    vertical-align: top;
  }

  th {
    background: #f7f7fa;
    font-weight: 600;
    color: #333;
    min-width: 140px;
    max-width: 260px;
    width: 180px;
    border-right: 2px solid #e0e0e0;
  }

  td {
    background: #fcfcfc;
    color: #222;
    font-weight: 400;
    transition: background 0.2s;
  }

  tr:hover td {
    background: #f0f7ff;
  }
}
.liver-image-title {
  padding-left: 16px;
}
.liver-images {
  display: grid;
  padding: 12px 16px;
  grid-template-columns: repeat(5, 136px);
  grid-gap: 32px;
  font-size: 12px;

  .file {
    cursor: pointer;

    .thumbnail {
      height: 100px;
      width: 136px;
      margin-bottom: 12px;
      border-radius: 4px;
      background-color: #fafbfc;
      box-shadow: inset 0 0 0 1px #b8c2cc;
      display: flex;
      align-items: center;
      justify-content: center;

      &:hover {
        box-shadow: 0 0 0 4px #bfdcff;
      }

      &.is-loading {
        cursor: default;

        &:hover {
          box-shadow: inset 0 0 0 1px #b8c2cc;
        }
      }
    }

    .image-loader {
      width: 36px;
      height: 36px;
      border: 4px solid #d1d5db;
      border-top-color: #2563eb;
      border-radius: 50%;
      animation: spin 0.9s linear infinite;
    }

    @keyframes spin {
      to {
        transform: rotate(360deg);
      }
    }

    .file-icon {
      font-size: 50px;
      color: #b8c2cc;
    }

    .thumbnail-img {
      height: 100px;
      width: 136px;
      object-fit: cover;
      border-radius: 4px;
      box-shadow: inset 0 0 0 1px #b8c2cc;
    }

    .thumbnail-details {
      .name {
        color: #272a2e;
        overflow: hidden;
        white-space: nowrap;
        font-weight: 400;
        text-overflow: ellipsis;
      }
    }
  }
}
.liver-images-padding {
  width: 16px;
  height: 1px;
  background: transparent;
  pointer-events: none;
  grid-column: span 1;
}
.filters-container {
  margin-top: 24px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
}
.filters {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 4px;
}

.filter-button {
  padding: 8px 12px;
  background-color: #eee;
  border-radius: 4px;
  white-space: nowrap;
  cursor: pointer;
}

.selected-filter-button {
  background-color: $primary-color;
  color: white;
}
@media (max-width: 768px) {
  .dashboard {
    padding: 0px;
  }

  .perfusion-info-container {
    border-radius: 0px;
    padding: 0px;
  }

  .graphs {
    margin-top: 16px;
    padding-left: 8px;
    padding-right: 8px;
    border-radius: 0px;
  }

  .graph-title-container {
    padding-top: 8px;
    padding-bottom: 8px;
  }

  .chart {
    display: grid;
    gap: 16px;
    grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  }

  .warning {
    margin-left: 8px;
    margin-right: 8px;
  }

  .matrix-table th,
  .matrix-table td {
    min-width: 80px;
    max-width: 120px;
    font-size: 13px;
    padding: 8px 6px;
  }
  .matrix-table th {
    min-width: 100px;
    max-width: 140px;
    width: 110px;
  }
}
.loader,
.no-perfusion-data {
  display: flex;
  align-items: center;
  height: 40vh;
  width: 100%;
  padding: 16px;
  font-weight: 700;
  font-size: 16px;
  justify-content: center;
}
</style>
